import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Eye, Calendar, Loader2 } from 'lucide-react';
import { DiamondLogo } from '../components/shared/DiamondLogo';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function BlogPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SEO meta
    document.title = 'The Diamond Scorer Blog · Coaching tips, scoring how-tos, baseball & softball insights';
    fetch(`${API}/api/articles`)
      .then((r) => r.json())
      .then((data) => setArticles(Array.isArray(data) ? data : []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-950 via-green-900 to-stone-900" data-testid="blog-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 pt-6 pb-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-amber-300 hover:text-amber-200 text-sm font-semibold mb-6"
          data-testid="blog-back-btn"
        >
          <ArrowLeft size={14} /> Back to Diamond Scorer
        </button>

        <div className="flex items-center gap-3 mb-2">
          <DiamondLogo size={36} />
          <h1
            className="text-4xl md:text-5xl font-extrabold text-white tracking-tight"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            THE <span className="text-amber-400 italic">DIAMOND</span> BLOG
          </h1>
        </div>
        <p className="text-green-100/80 text-base md:text-lg max-w-2xl">
          Coaching tips, scoring how-tos, lineup science, and the occasional dugout story — all written by the team behind Diamond Scorer.
        </p>
      </div>

      {/* Articles list */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-green-200 gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading articles…
          </div>
        ) : articles.length === 0 ? (
          <div className="bg-white/5 border border-green-700/30 rounded-2xl p-10 text-center" data-testid="blog-empty">
            <BookOpen size={36} className="text-amber-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              MORE STORIES COMING SOON
            </h2>
            <p className="text-green-200/70 text-sm">
              We're writing the first wave of coaching guides, scoring how-tos, and league-success stories. Check back next week.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {articles.map((a) => (
              <button
                key={a.id}
                onClick={() => navigate(`/blog/${a.slug}`)}
                className="text-left bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all group"
                data-testid={`blog-card-${a.slug}`}
              >
                {a.cover_image_url ? (
                  <div className="aspect-[16/9] bg-stone-200 overflow-hidden">
                    <img
                      src={a.cover_image_url}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-green-700 to-green-900 flex items-center justify-center">
                    <DiamondLogo size={48} />
                  </div>
                )}
                <div className="p-5">
                  <h2
                    className="text-xl font-bold text-stone-900 mb-2 leading-snug group-hover:text-green-800 transition"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {a.title}
                  </h2>
                  {a.excerpt && (
                    <p className="text-stone-600 text-sm mb-3 line-clamp-3">{a.excerpt}</p>
                  )}
                  <div className="flex items-center gap-3 text-[11px] text-stone-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={11} /> {formatDate(a.created_at)}
                    </span>
                    {typeof a.view_count === 'number' && a.view_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Eye size={11} /> {a.view_count}
                      </span>
                    )}
                    {a.author && <span>· by {a.author}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center pb-6">
        <div className="flex items-center justify-center gap-4 text-[11px] text-green-300/60 flex-wrap">
          <a href="/" className="hover:text-green-200 underline-offset-2 hover:underline">Home</a>
          <span>·</span>
          <a href="/contact" className="hover:text-green-200 underline-offset-2 hover:underline" data-testid="blog-footer-contact">Contact</a>
          <span>·</span>
          <a href="/privacy" className="hover:text-green-200 underline-offset-2 hover:underline">Privacy</a>
          <span>·</span>
          <a href="/terms" className="hover:text-green-200 underline-offset-2 hover:underline">Terms</a>
        </div>
        <p className="text-[10px] text-green-400/40 mt-2">Diamond Scorer &copy; {new Date().getFullYear()}</p>
      </div>
    </div>
  );
}
